import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface UpdateBalanceDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (amount: number, type: 'admin_add' | 'admin_deduct', notes?: string) => void;
  isLoading?: boolean;
}

export function UpdateBalanceDialog({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
}: UpdateBalanceDialogProps) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'admin_add' | 'admin_deduct'>('admin_add');
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (!amount || parseFloat(amount) <= 0) {
      return;
    }
    onConfirm(parseFloat(amount), type, notes || undefined);
    setAmount('');
    setNotes('');
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
      setAmount('');
      setNotes('');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in"
      onClick={handleBackdropClick}
    >
      <div
        className="bg-neutral-dark/95 backdrop-blur-lg rounded-2xl shadow-2xl border border-neutral-dark p-6 md:p-8 max-w-md w-full animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full text-brand-accent bg-brand-accent/20">
          <svg
            className="w-8 h-8"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>

        {/* Title */}
        <h3 className="text-2xl font-heading font-bold text-brand-accent mb-6 text-center">
          {t('admin.users.updateBalance.title') || 'Update Balance'}
        </h3>

        {/* Form */}
        <div className="space-y-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.users.updateBalance.amount') || 'Amount'}
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              disabled={isLoading}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.users.updateBalance.type') || 'Type'}
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as 'admin_add' | 'admin_deduct')}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="admin_add">
                {t('admin.users.updateBalance.add') || 'Add Balance'}
              </option>
              <option value="admin_deduct">
                {t('admin.users.updateBalance.deduct') || 'Deduct Balance'}
              </option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.users.updateBalance.notes') || 'Notes (Optional)'}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('admin.users.updateBalance.notesPlaceholder') || 'Add notes...'}
              rows={3}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => {
              onClose();
              setAmount('');
              setNotes('');
            }}
            disabled={isLoading}
            className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading || !amount || parseFloat(amount) <= 0}
            className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/80 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          >
            {isLoading ? t('common.loading') || 'Loading...' : t('common.confirm') || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

